import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CloudArrowUpIcon,
  FolderIcon,
  PhotoIcon,
  DocumentIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { MediaLibraryManager } from '../../components/media/MediaLibraryManager';
import { MediaUploader } from '../../components/media/MediaUploader';
import { MediaGallery } from '../../components/media/MediaGallery';
import {
  MediaLibrary,
  PimMedia,
  MediaFilters,
  PimMediaFormData
} from '../../types/pim';
import { MediaUploadData, MediaUploadProgress } from '../../types/media';
import { pimService } from '../../services/pimService';

const MediaEditForm: React.FC<{
  media: PimMedia;
  onSubmit: (data: Partial<PimMediaFormData>) => void;
  onCancel: () => void;
  loading?: boolean;
}> = ({ media, onSubmit, onCancel, loading = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<PimMediaFormData>>({
    title: media.title || '',
    alt_text: media.alt_text || '',
    description: media.description || '',
    tags: media.tags || [],
    is_public: media.is_public
  });

  const [tagInput, setTagInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('common.name')}
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('pim.media.altText')}
        </label>
        <input
          type="text"
          value={formData.alt_text}
          onChange={(e) => setFormData(prev => ({ ...prev, alt_text: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('common.description')}
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('pim.media.tags')}
        </label>
        <div className="flex space-x-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            placeholder={t('pim.media.addTag') || 'Add a tag...'}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
          <button
            type="button"
            onClick={addTag}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            disabled={loading}
          >
            {t('common.add')}
          </button>
        </div>
        {formData.tags && formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  disabled={loading}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_public"
          checked={formData.is_public}
          onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
          className="h-4 w-4 text-blue-600 rounded border-gray-300"
          disabled={loading}
        />
        <label htmlFor="is_public" className="ml-2 text-sm text-gray-700">
          {t('pim.media.isPublic') || 'Public (visible to all users)'}
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={loading}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? t('pim.common.saving') : t('pim.media.updateMedia') || 'Update Media'}
        </button>
      </div>
    </form>
  );
};

const MediaPreview: React.FC<{
  media: PimMedia;
  onClose: () => void;
}> = ({ media, onClose }) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <EyeIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white truncate">
                  {media.title || media.original_name}
                </h3>
                <p className="text-sm text-white/80">{media.mime} • {(media.size / 1024).toFixed(2)} KB</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Body with image/document preview */}
        <div className="p-6 max-h-[calc(95vh-200px)] overflow-y-auto">
          {media.kind === 'image' && media.url ? (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <img
                src={media.url}
                alt={media.alt_text || media.title || 'Media preview'}
                className="max-w-full max-h-[65vh] object-contain mx-auto rounded-lg shadow-lg"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
              <DocumentIcon className="h-20 w-20 text-gray-400 mb-4" />
              <p className="text-xl font-semibold text-gray-900">{media.original_name}</p>
              <p className="text-sm text-gray-500 mt-1">{media.mime}</p>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {media.title && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wide mb-1">Title</h4>
                <p className="text-sm text-blue-800">{media.title}</p>
              </div>
            )}

            {media.alt_text && (
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <h4 className="text-xs font-bold text-green-900 uppercase tracking-wide mb-1">Alt Text</h4>
                <p className="text-sm text-green-800">{media.alt_text}</p>
              </div>
            )}
          </div>

          {media.description && (
            <div className="mt-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
              <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wide mb-2">{t('common.description')}</h4>
              <p className="text-sm text-purple-800 leading-relaxed">{media.description}</p>
            </div>
          )}

          {media.tags && media.tags.length > 0 && (
            <div className="mt-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
              <h4 className="text-xs font-bold text-yellow-900 uppercase tracking-wide mb-2">{t('pim.media.tags')}</h4>
              <div className="flex flex-wrap gap-2">
                {media.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 text-sm font-medium bg-yellow-200 text-yellow-900 rounded-full border border-yellow-300"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MediaManagement: React.FC = () => {
  const { t } = useTranslation();
  const [selectedLibrary, setSelectedLibrary] = useState<MediaLibrary | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [editingMedia, setEditingMedia] = useState<PimMedia | null>(null);
  const [previewMedia, setPreviewMedia] = useState<PimMedia | null>(null);
  const [deletingMedia, setDeletingMedia] = useState<PimMedia | null>(null);
  const [uploadProgress, setUploadProgress] = useState<MediaUploadProgress[]>([]);
  const [mediaFilters, setMediaFilters] = useState<MediaFilters>({});

  const queryClient = useQueryClient();

  // Build filters including library
  const filters: MediaFilters = {
    ...mediaFilters,
    ...(selectedLibrary && { library_id: selectedLibrary.id })
  };

  // Fetch media
  const { data: mediaResponse, isLoading } = useQuery({
    queryKey: ['media', filters],
    queryFn: () => pimService.getMedia(filters)
  });

  const media = Array.isArray(mediaResponse?.data?.media)
    ? mediaResponse.data.media
    : [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (uploadData: MediaUploadData[]) => {
      const results = [];
      for (const data of uploadData) {
        const result = await pimService.uploadMedia(data);
        results.push(result);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setShowUploader(false);
    }
  });

  // Update media mutation
  const updateMediaMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PimMediaFormData> }) =>
      pimService.updateMedia(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      setEditingMedia(null);
    }
  });

  // Delete media mutation
  const deleteMediaMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteMedia(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    }
  });

  const handleUpload = useCallback(async (uploadData: MediaUploadData[]) => {
    return uploadMutation.mutateAsync(uploadData);
  }, [uploadMutation]);

  const handleMediaEdit = (media: PimMedia) => {
    setEditingMedia(media);
  };

  const handleMediaUpdate = (data: Partial<PimMediaFormData>) => {
    if (editingMedia) {
      updateMediaMutation.mutate({ id: editingMedia.id, data });
    }
  };

  const handleMediaDelete = (media: PimMedia) => {
    setDeletingMedia(media);
  };

  const confirmDelete = () => {
    if (deletingMedia) {
      deleteMediaMutation.mutate(deletingMedia.id);
      setDeletingMedia(null);
    }
  };

  const handleMediaDownload = (media: PimMedia) => {
    if (media.url) {
      const link = document.createElement('a');
      link.href = media.url;
      link.download = media.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col xl:flex-row gap-4 sm:gap-6">
        {/* Sidebar - Libraries */}
        <div className="xl:w-64 flex-shrink-0">
          <MediaLibraryManager
            selectedLibraryId={selectedLibrary?.id}
            onSelectLibrary={setSelectedLibrary}
          />
        </div>

        {/* Main Content - Media Gallery */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900">{t('pim.media.allLibraries')}</h2>
              <p className="text-sm text-gray-600">{t('pim.media.subtitle')}</p>
            </div>
            <button
              onClick={() => setShowUploader(true)}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 whitespace-nowrap"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              {t('pim.media.uploadMedia')}
            </button>
          </div>

          {/* Media Gallery */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 min-h-[600px]">
            <MediaGallery
              media={media}
              loading={isLoading}
              onEdit={handleMediaEdit}
              onDelete={handleMediaDelete}
              onDownload={handleMediaDownload}
              onPreview={setPreviewMedia}
              filters={mediaFilters}
              onFiltersChange={setMediaFilters}
            />
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setEditingMedia(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <PencilIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('pim.media.edit')}</h3>
                    <p className="text-sm text-white/80">{editingMedia.original_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setEditingMedia(null)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <MediaEditForm
                media={editingMedia}
                onSubmit={handleMediaUpdate}
                onCancel={() => setEditingMedia(null)}
                loading={updateMediaMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewMedia && (
        <MediaPreview
          media={previewMedia}
          onClose={() => setPreviewMedia(null)}
        />
      )}

      {/* Upload Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('pim.media.uploadMedia') || 'Upload Media'}</h3>
              <button
                onClick={() => setShowUploader(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">{t('common.close')}</span>
                ×
              </button>
            </div>
            <MediaUploader
              libraryId={selectedLibrary?.id}
              onUpload={handleUpload}
              onProgress={setUploadProgress}
              uploadProgress={uploadProgress}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setDeletingMedia(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <TrashIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{t('pim.media.deleteConfirm') || 'Confirm Delete'}</h3>
                  <p className="text-sm text-white/80">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-900">
                  Are you sure you want to delete <strong className="font-semibold">{deletingMedia.title || deletingMedia.original_name}</strong>?
                </p>
              </div>

              {deletingMedia.kind === 'image' && deletingMedia.thumbnail_url && (
                <div className="mb-4">
                  <img
                    src={deletingMedia.thumbnail_url}
                    alt={deletingMedia.title || 'Preview'}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingMedia(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMediaMutation.isPending}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteMediaMutation.isPending ? 'Deleting...' : (t('common.delete') || 'Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaManagement;